import React, { useEffect, useState } from 'react';
import { TextField, Button, Box, Grid, Divider, Autocomplete, FormControl, Backdrop, CircularProgress } from '@mui/material';
import { jsonConfiguration } from '../helpers/jsonConfiguration';
import { ErrorSwal } from '../../../shared/helpers/swal';
import { getFormByType } from '../helpers/formsData';
import { selectJsonFormatter } from '../helpers/jsonFormatter';
import { checkCompanyData } from '../helpers/checkData';

const FormularioGenerico = ({ user, type }) => {
    // Estado para almacenar la información de los formularios
    const [formData, setFormData] = useState({});
    const [errors, setErrors] = useState({}); // Track validation errors
    const [autocompleteData, setAutocompleteData] = useState({});
    const [loading, setLoading] = useState(false);
    const formularios = getFormByType(type);

    useEffect(() => {
        setLoading(true);
        const fetchAutocompleteData = async () => {
            const newAutocompleteData = {};
            for (const formulario of formularios) {
                if (formulario.secciones) {
                    for (const seccion of formulario.secciones) {
                        for (const campo of seccion.campos) {
                            if (campo.autocomplete) {
                                newAutocompleteData[campo.name] = await selectJsonFormatter(campo.name, user);
                            }
                        }
                    }
                } else {
                    for (const campo of formulario.campos) {
                        if (campo.autocomplete) {
                            newAutocompleteData[campo.name] = await selectJsonFormatter(campo.name, user);
                        }
                    }
                }
            }
            setAutocompleteData(newAutocompleteData);
        };

        fetchAutocompleteData();
        setLoading(false);
    }, [formularios]);


    // Manejar los cambios en los inputs
    const handleChange = (e, tipo) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [tipo]: {
                ...prev[tipo],
                [name]: value,
            },
        }));
        setErrors((prev) => ({
            ...prev,
            [tipo]: {
                ...prev[tipo],
                [name]: !value,
            },
        }));
    };

    // Manejar los cambios en el Autocomplete
    const handleAutocompleteChange = (event, value, campo, tipo) => {
        setFormData((prev) => ({
            ...prev,
            [tipo]: {
                ...prev[tipo],
                [campo.name]: value ? { nombre: value.nombre, id: value.id } : { nombre: '', id: '' },
            },
        }));
        setErrors((prev) => ({
            ...prev,
            [tipo]: {
                ...prev[tipo],
                [campo.name]: !value,
            },
        }));
    };

    const validateForm = (tipo) => {
        const data = formData[tipo];
        console.log('data', data);
        const newErrors = {};

        const formulario = formularios.find(formulario => formulario.tipo === tipo);
        if (formulario.secciones) {
            formulario.secciones.forEach(seccion => {
                seccion.campos.forEach(campo => {
                    if (campo.required && (!data || !data[campo.name])) {
                        if (!newErrors[tipo]) newErrors[tipo] = {};
                        newErrors[tipo][campo.name] = true;
                    }
                });
            });
        } else {
            formulario.campos.forEach(campo => {
                console.log('campo', campo);
                if (campo.required && (!data || !data[campo.name])) {
                    if (!newErrors[tipo]) newErrors[tipo] = {};
                    newErrors[tipo][campo.name] = true;
                }
            });
        }

        setErrors((prev) => ({
            ...prev,
            [tipo]: newErrors[tipo] || {},
        }));

        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = (e, tipo) => {
        e.preventDefault(); // Prevent default form submission behavior

        if (!validateForm(tipo)) {
            ErrorSwal('Error', 'Verifique que los campos esten completados', false);
            return;
        }

        const data = formData[tipo]; // obtener los datos del formulario específico
        const jsonData = jsonConfiguration(tipo, data);
        
        if (tipo === 'conductor') {
            const validation = checkCompanyData(jsonData);
            if (!validation.valid) {
                ErrorSwal('Error', validation.message, false);
                return;
            }
        }

        console.log(`Formulario de ${tipo} enviado con los datos:`, jsonData);
    };

    return (
        <div>
            {loading && (
                <Backdrop
                    sx={{ color: '#fff' }}
                    style={{ zIndex: 999 }}
                    open={loading}
                >
                    <CircularProgress />
                </Backdrop>
            )}
            {formularios.map((formulario, index) => (
                <Box key={index} mb={4}>
                    <h3>{`Formulario de ${formulario.title || formulario.tipo}`}</h3>
                    <FormControl
                        component="form"
                        style={{ width: '100%' }}
                        onSubmit={(e) => handleSubmit(e, formulario.tipo)}
                    >
                        {/* Si existen secciones en el formulario */}
                        {formulario.secciones ? (
                            formulario.secciones.map((seccion, secIndex) => (
                                <Box key={secIndex} mb={2}>
                                    <h4>{seccion.nombre}</h4>
                                    <Grid container spacing={2}>
                                        {seccion.campos.map((campo, idx) => (
                                            <Grid
                                                key={idx}
                                                item
                                                xs={12}  // Pantallas pequeñas
                                                sm={6}   // Pantallas medianas y grandes
                                            >
                                                {campo.autocomplete ? (
                                                    // Si el campo tiene autocomplete, renderizar el Autocomplete de MUI
                                                    <Autocomplete
                                                        // options={campo.data}
                                                        options={autocompleteData[campo.name] || []}
                                                        getOptionLabel={(option) => option.nombre}
                                                        value={formData[formulario.tipo]?.[campo.name] || null}
                                                        isOptionEqualToValue={(option, value) => option.id === value.id}
                                                        onChange={(event, value) => handleAutocompleteChange(event, value, campo, formulario.tipo)}
                                                        renderInput={(params) => (
                                                            <TextField
                                                                {...params}
                                                                label={campo.label}
                                                                variant="outlined"
                                                                margin="normal"
                                                                required={campo.required || false}
                                                                error={!!errors[formulario.tipo]?.[campo.name]}
                                                                helperText={errors[formulario.tipo]?.[campo.name] ? 'Este campo es requerido' : ''}
                                                            />
                                                        )}
                                                    />
                                                ) : (
                                                    // Si no es autocomplete, renderizar el TextField
                                                    <TextField
                                                        fullWidth
                                                        label={campo.label}
                                                        type={campo.type}
                                                        name={campo.name}
                                                        variant="outlined"
                                                        margin="normal"
                                                        required={campo.required || false}
                                                        error={!!errors[formulario.tipo]?.[campo.name]}
                                                        helperText={errors[formulario.tipo]?.[campo.name] ? 'Este campo es requerido' : ''}
                                                        onChange={(e) => handleChange(e, formulario.tipo)}
                                                    />
                                                )}
                                            </Grid>
                                        ))}
                                    </Grid>
                                    {secIndex < formulario.secciones.length - 1 && <Divider sx={{ my: 2 }} />}
                                </Box>
                            ))
                        ) : (
                            <Box mb={2}>
                                <Grid container spacing={2}>
                                    {formulario.campos.map((campo, idx) => (
                                        <Grid
                                            key={idx}
                                            item
                                            xs={12}// Pantallas pequeñas
                                            sm={formulario.campos.length > 1 ? 6 : 12}   // Pantallas medianas y grandes
                                        >
                                            {campo.autocomplete ? (
                                                <Autocomplete
                                                    // options={campo.data}
                                                    options={autocompleteData[campo.name] || []}
                                                    getOptionLabel={(option) => option.nombre}
                                                    value={formData[formulario.tipo]?.[campo.name] || null}
                                                    isOptionEqualToValue={(option, value) => option.id === value.id}
                                                    onChange={(event, value) => handleAutocompleteChange(event, value, campo, formulario.tipo)}
                                                    renderInput={(params) => (
                                                        <TextField
                                                            {...params}
                                                            label={campo.label}
                                                            variant="outlined"
                                                            margin="normal"
                                                            required={campo.required || false}
                                                            error={!!errors[formulario.tipo]?.[campo.name]}
                                                            helperText={errors[formulario.tipo]?.[campo.name] ? 'Este campo es requerido' : ''}
                                                        />
                                                    )}
                                                />
                                            ) : (
                                                <TextField
                                                    fullWidth
                                                    label={campo.label}
                                                    type={campo.type}
                                                    name={campo.name}
                                                    // variant="outlined"
                                                    margin="normal"
                                                    required={campo.required || false}
                                                    error={!!errors[formulario.tipo]?.[campo.name]}
                                                    helperText={errors[formulario.tipo]?.[campo.name] ? 'Este campo es requerido' : ''}
                                                    onChange={(e) => handleChange(e, formulario.tipo)}
                                                />
                                            )}
                                        </Grid>
                                    ))}
                                </Grid>
                            </Box>
                        )}

                        <button
                            variant="contained"
                            className="btn btn-primary"
                            type="submit"
                            sx={{ mt: 2 }}
                        >
                            {formulario.boton ? formulario.boton : 'Guardar'}
                        </button>
                    </FormControl>
                    <Divider sx={{ my: 2 }} />
                </Box>
            ))}
        </div>
    );
};

export default FormularioGenerico;